import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { Download, FileUp, Star, Users, ThumbsUp, Eye, TrendingUp, ArrowLeft, ChevronsUpDown, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";

const EventoCombobox = ({ eventos, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const sel = eventos.find((e) => e.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open}
          className="w-full justify-between font-normal" data-testid="filtro-evento">
          <span className="truncate">{value === "todos" ? "Todos los eventos" : sel?.nombre || "Todos los eventos"}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command filter={(val, search) => (val || "").toLowerCase().includes((search || "").toLowerCase()) ? 1 : 0}>
          <CommandInput placeholder="Escribe para buscar evento…" data-testid="filtro-evento-busqueda" />
          <CommandList>
            <CommandEmpty>Sin coincidencias</CommandEmpty>
            <CommandGroup>
              <CommandItem value="todos los eventos" data-testid="filtro-evento-todos"
                onSelect={() => { onChange("todos"); setOpen(false); }}>
                <Check className={`mr-2 h-4 w-4 ${value === "todos" ? "opacity-100" : "opacity-0"}`} />
                Todos los eventos
              </CommandItem>
              {eventos.map((ev) => (
                <CommandItem key={ev.id} value={`${ev.nombre} ${ev.cliente || ""}`} data-testid={`filtro-evento-op-${ev.codigo}`}
                  onSelect={() => { onChange(ev.id); setOpen(false); }}>
                  <Check className={`mr-2 h-4 w-4 shrink-0 ${value === ev.id ? "opacity-100" : "opacity-0"}`} />
                  <span className="w-2.5 h-2.5 rounded-full mr-2 shrink-0" style={{ background: ev.paleta?.base || ev.color_base }} />
                  <span className="truncate">{ev.nombre}</span>
                  <span className="ml-2 text-xs text-zinc-400 truncate">{ev.cliente}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const Stars = ({ value, size = "w-5 h-5" }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star key={n} className={`${size} ${Math.round(value || 0) >= n ? "fill-amber-400 text-amber-400" : "fill-zinc-200 text-zinc-200"}`} />
    ))}
  </div>
);

const Kpi = ({ icon: Icon, label, children, testid }) => (
  <div className="bg-white border border-zinc-200 rounded-2xl p-5" data-testid={testid}>
    <div className="flex items-center gap-2 text-zinc-400 text-xs font-bold uppercase tracking-wide mb-2">
      <Icon className="w-4 h-4" /> {label}
    </div>
    {children}
  </div>
);

const ImportModal = ({ open, onClose, onDone }) => {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef();

  useEffect(() => { if (open) { setFile(null); setResult(null); } }, [open]);

  const importar = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/encuestas/import`, fd);
      setResult(data);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al importar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="import-excel-modal">
        <DialogHeader><DialogTitle>Importar Excel (sistema legado)</DialogTitle></DialogHeader>
        {result ? (
          <div className="space-y-2 text-sm" data-testid="import-resumen">
            <p>✅ Creadas: <b>{result.creadas}</b></p>
            <p>↩️ Duplicadas saltadas: <b>{result.duplicadas_saltadas}</b></p>
            {result.eventos_creados?.length > 0 && <p>🆕 Eventos creados: <b>{result.eventos_creados.join(", ")}</b></p>}
            <p>👁 Visitas importadas: <b>{result.visitas_importadas}</b></p>
            {result.errores?.length > 0 && (
              <div className="text-red-600 max-h-32 overflow-y-auto border border-red-200 rounded p-2">
                {result.errores.map((e, i) => <p key={i}>{e}</p>)}
              </div>
            )}
            <Button className="w-full mt-2" onClick={onClose} data-testid="import-cerrar-btn">Cerrar</Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div
              className="border-2 border-dashed border-zinc-300 rounded-xl p-8 text-center cursor-pointer hover:border-zinc-400 transition-colors"
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); e.dataTransfer.files?.[0] && setFile(e.dataTransfer.files[0]); }}
              data-testid="import-dropzone"
            >
              <FileUp className="w-8 h-8 mx-auto text-zinc-300 mb-2" />
              {file ? <p className="text-sm font-semibold text-zinc-700">{file.name}</p>
                : <p className="text-sm text-zinc-500">Arrastra el .xlsx aquí o haz clic para seleccionar</p>}
              <p className="text-xs text-zinc-400 mt-1">Export del Google Sheet legado (hoja "Respuestas")</p>
              <input ref={inputRef} type="file" accept=".xlsx" className="hidden"
                onChange={(e) => e.target.files?.[0] && setFile(e.target.files[0])} data-testid="import-file-input" />
            </div>
            <Button className="w-full bg-[#0037ff] hover:bg-[#0037ff]/90" disabled={!file || busy} onClick={importar} data-testid="import-ejecutar-btn">
              {busy ? "Importando…" : "Importar"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function EncuestasDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const [eventos, setEventos] = useState([]);
  const [filtros, setFiltros] = useState({ evento_id: "todos", q: "", recomienda: "todos", desde: "", hasta: "" });
  const [data, setData] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/encuestas/eventos`).then(({ data }) => setEventos(data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    const params = {};
    if (filtros.evento_id !== "todos") params.evento_id = filtros.evento_id;
    if (filtros.q.trim()) params.q = filtros.q.trim();
    if (filtros.recomienda !== "todos") params.recomienda = filtros.recomienda;
    if (filtros.desde) params.desde = filtros.desde;
    if (filtros.hasta) params.hasta = filtros.hasta;
    try {
      const { data } = await axios.get(`${API}/encuestas/dashboard`, { params });
      setData(data);
    } catch {
      toast.error("No se pudo cargar el dashboard");
    }
  }, [filtros]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const exportPdf = async () => {
    setPdfBusy(true);
    try {
      const params = filtros.evento_id !== "todos" ? { evento_id: filtros.evento_id } : {};
      const res = await axios.get(`${API}/encuestas/dashboard/pdf`, { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = (res.headers["content-disposition"]?.match(/filename="(.+)"/) || [])[1] || "reporte-encuestas.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("No se pudo generar el PDF");
    } finally {
      setPdfBusy(false);
    }
  };

  const chartData = (() => {
    if (!data) return [];
    const map = Object.fromEntries(data.visitas_por_dia);
    const days = [];
    for (let i = 13; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const key = d.toISOString().slice(0, 10);
      days.push({ dia: key.slice(5), visitas: map[key] || 0 });
    }
    return days;
  })();

  const fmtFecha = (iso) => {
    try {
      return new Date(iso).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" });
    } catch { return (iso || "").slice(0, 10); }
  };

  const comentarios = (data?.respuestas || []).filter((r) => (r.comentarios || "").trim());

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto" data-testid="encuestas-dashboard-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/clientes/encuestas")} data-testid="volver-eventos-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-extrabold text-zinc-900" style={{ fontFamily: "'Mulish', sans-serif" }}>Dashboard · Encuestas de Calidad</h1>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportPdf} disabled={pdfBusy} data-testid="exportar-pdf-btn">
            <Download className="w-4 h-4 mr-2" /> {pdfBusy ? "Generando…" : "Exportar PDF"}
          </Button>
          {isAdmin && (
            <Button onClick={() => setImportOpen(true)} className="bg-[#0037ff] hover:bg-[#0037ff]/90" data-testid="importar-excel-btn">
              <FileUp className="w-4 h-4 mr-2" /> Importar Excel
            </Button>
          )}
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white border border-zinc-200 rounded-2xl p-4 mb-6 grid grid-cols-12 gap-3 items-end">
        <div className="col-span-12 sm:col-span-4">
          <label className="text-xs text-zinc-500 font-semibold">Evento</label>
          <EventoCombobox eventos={eventos} value={filtros.evento_id}
            onChange={(v) => setFiltros({ ...filtros, evento_id: v })} />
        </div>
        <div className="col-span-12 sm:col-span-3">
          <label className="text-xs text-zinc-500 font-semibold">Búsqueda</label>
          <Input placeholder="Nombre, empresa, email…" value={filtros.q} onChange={(e) => setFiltros({ ...filtros, q: e.target.value })} data-testid="filtro-busqueda" />
        </div>
        <div className="col-span-6 sm:col-span-2">
          <label className="text-xs text-zinc-500 font-semibold">Recomienda</label>
          <Select value={filtros.recomienda} onValueChange={(v) => setFiltros({ ...filtros, recomienda: v })}>
            <SelectTrigger data-testid="filtro-recomienda"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos</SelectItem>
              <SelectItem value="si">Sí</SelectItem>
              <SelectItem value="no">No</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-6 sm:col-span-3 grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs text-zinc-500 font-semibold">Desde</label>
            <Input type="date" value={filtros.desde} onChange={(e) => setFiltros({ ...filtros, desde: e.target.value })} data-testid="filtro-desde" />
          </div>
          <div>
            <label className="text-xs text-zinc-500 font-semibold">Hasta</label>
            <Input type="date" value={filtros.hasta} onChange={(e) => setFiltros({ ...filtros, hasta: e.target.value })} data-testid="filtro-hasta" />
          </div>
        </div>
      </div>

      {!data ? (
        <div className="py-20 text-center text-zinc-400">Cargando…</div>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid sm:grid-cols-3 gap-4 mb-4">
            <Kpi icon={Star} label="Promedio global" testid="kpi-promedio-global">
              <div className="flex items-center gap-3">
                <span className="text-3xl font-extrabold text-zinc-900">{data.promedio_global ?? "—"}</span>
                <Stars value={data.promedio_global} />
              </div>
            </Kpi>
            <Kpi icon={Users} label="Total respuestas" testid="kpi-total-respuestas">
              <span className="text-3xl font-extrabold text-zinc-900">{data.total_respuestas}</span>
            </Kpi>
            <Kpi icon={ThumbsUp} label="Recomendarían" testid="kpi-recomiendan">
              <span className="text-3xl font-extrabold text-zinc-900">{data.pct_recomienda != null ? `${data.pct_recomienda}%` : "—"}</span>
              <span className="text-sm text-zinc-400 ml-2">{data.recomienda_n} de {data.total_respuestas}</span>
            </Kpi>
          </div>

          {/* Visitas */}
          <div className="grid sm:grid-cols-3 gap-4 mb-4">
            <div className="space-y-4">
              <Kpi icon={Eye} label="Total visitas" testid="kpi-visitas">
                <span className="text-3xl font-extrabold text-zinc-900">{data.visitas_total}</span>
              </Kpi>
              <Kpi icon={TrendingUp} label="Tasa de conversión" testid="kpi-conversion">
                <span className="text-3xl font-extrabold text-zinc-900">{data.conversion != null ? `${data.conversion}%` : "—"}</span>
                <p className="text-xs text-zinc-400 mt-1">respuestas / visitas</p>
              </Kpi>
              <Kpi icon={TrendingUp} label="Tasa contestada" testid="kpi-tasa-contestada">
                <span className="text-3xl font-extrabold text-zinc-900">{data.tasa_contestada != null ? `${data.tasa_contestada}%` : "—"}</span>
                <p className="text-xs text-zinc-400 mt-1">respuestas / total a encuestar {data.total_a_encuestar ? `(${data.total_a_encuestar})` : ""}</p>
              </Kpi>
            </div>
            <div className="sm:col-span-2 bg-white border border-zinc-200 rounded-2xl p-5" data-testid="grafica-visitas">
              <p className="text-xs font-bold uppercase tracking-wide text-zinc-400 mb-3">Visitas por día (últimos 14 días)</p>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={chartData}>
                  <XAxis dataKey="dia" tick={{ fontSize: 10 }} stroke="#a1a1aa" />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10 }} stroke="#a1a1aa" width={26} />
                  <Tooltip />
                  <Bar dataKey="visitas" fill="#0037ff" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Promedios por categoría */}
          <div className="grid sm:grid-cols-3 gap-4 mb-6">
            {[["experiencia_general", "Experiencia general"], ["comunicacion", "Comunicación"], ["eficiencia", "Eficiencia"]].map(([k, label]) => (
              <div key={k} className="bg-white border border-zinc-200 rounded-2xl p-4 flex items-center justify-between" data-testid={`kpi-cat-${k}`}>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-zinc-400">{label}</p>
                  <p className="text-2xl font-extrabold text-zinc-900 mt-1">{data.promedios[k] ?? "—"}</p>
                </div>
                <Stars value={data.promedios[k]} size="w-4 h-4" />
              </div>
            ))}
          </div>

          {/* Tabla */}
          <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden mb-6">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="tabla-respuestas">
                <thead>
                  <tr className="bg-zinc-50 text-left text-xs uppercase tracking-wide text-zinc-500">
                    <th className="px-4 py-3">Nombre</th>
                    <th className="px-4 py-3">Empresa</th>
                    <th className="px-4 py-3">Evento</th>
                    <th className="px-4 py-3">Fecha</th>
                    <th className="px-3 py-3 text-center">Gral</th>
                    <th className="px-3 py-3 text-center">Com</th>
                    <th className="px-3 py-3 text-center">Efi</th>
                    <th className="px-3 py-3 text-center">Rec</th>
                  </tr>
                </thead>
                <tbody>
                  {data.respuestas.length === 0 ? (
                    <tr><td colSpan={8} className="px-4 py-10 text-center text-zinc-400">Sin respuestas con estos filtros</td></tr>
                  ) : data.respuestas.map((r) => (
                    <tr key={r.id} className="border-t border-zinc-100 hover:bg-zinc-50/60">
                      <td className="px-4 py-2.5 font-semibold text-zinc-800">{r.nombre}</td>
                      <td className="px-4 py-2.5 text-zinc-600">{r.empresa}</td>
                      <td className="px-4 py-2.5 text-zinc-500 text-xs">{r.evento_nombre}</td>
                      <td className="px-4 py-2.5 text-zinc-500 whitespace-nowrap">{fmtFecha(r.created_at)}</td>
                      <td className="px-3 py-2.5 text-center font-bold">{r.experiencia_general}</td>
                      <td className="px-3 py-2.5 text-center font-bold">{r.comunicacion}</td>
                      <td className="px-3 py-2.5 text-center font-bold">{r.eficiencia}</td>
                      <td className="px-3 py-2.5 text-center">{r.recomienda ? "✅" : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Comentarios */}
          {comentarios.length > 0 && (
            <div className="mb-10">
              <h2 className="text-base font-bold text-zinc-800 mb-3">Comentarios ({comentarios.length})</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {comentarios.map((r) => (
                  <div key={r.id} className="bg-white border border-zinc-200 rounded-2xl p-4" data-testid={`comentario-${r.id}`}>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="w-9 h-9 rounded-full bg-[#0037ff] text-white text-xs font-bold flex items-center justify-center">
                        {(r.nombre || "?").split(" ").slice(0, 2).map((p) => p[0]).join("").toUpperCase()}
                      </span>
                      <div>
                        <p className="text-sm font-bold text-zinc-800">{r.nombre}</p>
                        <p className="text-xs text-zinc-400">{r.empresa}</p>
                      </div>
                    </div>
                    <p className="text-sm text-zinc-600 leading-relaxed">{r.comentarios}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      <ImportModal open={importOpen} onClose={() => setImportOpen(false)} onDone={load} />
    </div>
  );
}
